import Swal from "sweetalert2";
import { Store } from "@ngrx/store";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { Blog, Picklist } from "src/app/shared/types/blog-types";
import { TranslateService } from "@ngx-translate/core";
import { AppState } from "src/app/store/index.selector";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { BlogService } from "src/app/shared/services/blog.service";
import { TableService } from "src/app/shared/services/table.service";
import { BaseTableComponent, DatatableColumn } from "src/app/shared/components/base-table/base-table.component";
import { Observable } from "rxjs";
import { PicklistService } from "src/app/shared/services/picklist.service";
import { PicklistType } from "src/app/shared/enums/picklist-types";
import { MessagePortalComponent } from "src/app/portal/portal";
import { PositioningService } from "src/app/shared/services/positioning.service";
import { toggleAnimation } from "src/app/shared/animations";

@Component({
  templateUrl: "./blogs.component.html",
  standalone: false,
  animations: [toggleAnimation],
})
export class BlogsComponent extends BaseTableComponent<Blog> implements OnInit {
  searchText = "";
  public languageOptions$!: Observable<Picklist[]>;
  public appOptions$!: Observable<Picklist[]>;
  @ViewChild("dropdownPlaceholder") dropdownPlaceholder!: ElementRef;
  @ViewChild(MessagePortalComponent) messagePortalComponent!: MessagePortalComponent;

  selectedLanguage!: string;
  selectedApplication!: string;

  constructor(
    private blogService: BlogService,
    public override translate: TranslateService,
    public override store: Store<AppState>,
    public override tableService: TableService,
    public picklistService: PicklistService,
    private positioningService: PositioningService,
  ) {
    super(store, tableService, `blogs/paginated`, translate);
  }

  @ViewChild("datatable") datatable: any;

  override ngOnInit(): void {
    super.ngOnInit();
    this.languageOptions$ = this.picklistService.getPicklistOptions(PicklistType.Language);
    this.appOptions$ = this.picklistService.getPicklistOptions(PicklistType.Application);
  }

  getColumns(): void {
    this.cols = [
      { field: "id", title: "Id", hide: true } as DatatableColumn,
      { field: "title", title: this.translate.instant("blogs.title") } as DatatableColumn,
      { field: "content", title: this.translate.instant("blogs.content"), hide: true } as DatatableColumn,
      { field: "category", title: this.translate.instant("blogs.category") } as DatatableColumn,
      { field: "language", title: this.translate.instant("blogs.language"), sort: false } as DatatableColumn,
      { field: "application", title: this.translate.instant("blogs.application"), sort: false } as DatatableColumn,
      { field: "created", title: this.translate.instant("blogs.created") } as DatatableColumn,
      { field: "updatedAt", title: this.translate.instant("blogs.updated_at") } as DatatableColumn,
      { field: "isTutorial", title: this.translate.instant("blogs.is_tutorial"), sort: false, hide: true } as DatatableColumn,
      { field: "actions", title: this.translate.instant("blogs.actions"), sort: false } as DatatableColumn,
    ];
  }

  handleDelete(id: number) {
    Swal.fire({
      icon: "warning",
      title: this.translate.instant("blogs.confirm_delete_blog"),
      showCancelButton: true,
      confirmButtonText: this.translate.instant("blogs.yes"),
      cancelButtonText: this.translate.instant("blogs.no"),
      customClass: {
        confirmButton: "swal2-confirm btn btn-primary",
        cancelButton: "swal2-cancel btn btn-secondary",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        const deleteSubscription = this.blogService.deleteBlog(id).subscribe({
          next: () => {
            deleteSubscription.unsubscribe();
            this.loading = false;
            showMessage(PopupType.Success, this.translate.instant("blogs.blog_deleted"));
            this.fetchItems(this.getPaginationParams());
          },
          error: (error) => {
            deleteSubscription.unsubscribe();
            this.loading = false;
            showMessage(PopupType.Danger, this.translate.instant("blogs.invalid_blog_deleted"));
            console.error("Error making DELETE request:", error);
          },
        });
      }
    });
  }

  setSearchText(text: string) {
    setTimeout(() => {
      this.search = text;
    });
  }

  onFilterChange() {
    this.application = this.selectedApplication ?? "";
    this.language = this.selectedLanguage ?? "";
    this.fetchItems(this.getPaginationParams());
  }

  onPortalAttached() {
    setTimeout(() => {
      this.positioningService.setPosition(this.dropdownPlaceholder, this.messagePortalComponent);
    }, 0);
  }
}
